import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, FieldArray, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CircularProgress from "@material-ui/core/CircularProgress";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	phoneRow: {
		display: "flex",
		gap: theme.spacing(1),
		alignItems: "flex-start",
	},
	countryField: {
		minWidth: 180,
	},
	textField: {
		marginRight: theme.spacing(1),
		flex: 1,
	},

	extraAttr: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
}));

const COUNTRY_OPTIONS = [
	{ code: "57", label: "Colombia (+57)" },
	{ code: "52", label: "Mexico (+52)" },
	{ code: "1", label: "United States / Canada (+1)" },
	{ code: "54", label: "Argentina (+54)" },
	{ code: "55", label: "Brazil (+55)" },
	{ code: "56", label: "Chile (+56)" },
	{ code: "51", label: "Peru (+51)" },
	{ code: "593", label: "Ecuador (+593)" },
	{ code: "58", label: "Venezuela (+58)" },
	{ code: "507", label: "Panama (+507)" },
	{ code: "506", label: "Costa Rica (+506)" },
	{ code: "502", label: "Guatemala (+502)" },
	{ code: "503", label: "El Salvador (+503)" },
	{ code: "504", label: "Honduras (+504)" },
	{ code: "505", label: "Nicaragua (+505)" },
	{ code: "591", label: "Bolivia (+591)" },
	{ code: "595", label: "Paraguay (+595)" },
	{ code: "598", label: "Uruguay (+598)" },
	{ code: "34", label: "Spain (+34)" },
	{ code: "351", label: "Portugal (+351)" },
];

const DEFAULT_COUNTRY_CODE = "57";

const sanitizeDigits = value => (value || "").replace(/\D/g, "");

const splitPhoneNumber = rawNumber => {
	const digits = sanitizeDigits(rawNumber);

	if (!digits) {
		return {
			countryCode: DEFAULT_COUNTRY_CODE,
			number: "",
		};
	}

	const countryMatch = [...COUNTRY_OPTIONS]
		.sort((left, right) => right.code.length - left.code.length)
		.find(option => digits.startsWith(option.code) && digits.length > option.code.length + 4);

	if (countryMatch) {
		return {
			countryCode: countryMatch.code,
			number: digits.slice(countryMatch.code.length),
		};
	}

	return {
		countryCode: DEFAULT_COUNTRY_CODE,
		number: digits,
	};
};

const buildContactState = values => {
	const phoneFields = splitPhoneNumber(values?.number);
	const currentName = values?.name || "";
	const username = values?.username
		? (values.username.startsWith("@") ? values.username : `@${values.username}`)
		: "";
	const editableName = username && !/[\p{L}\p{N}]/u.test(currentName)
		? username
		: currentName;

	return {
		name: editableName,
		number: phoneFields.number,
		countryCode: values?.countryCode || phoneFields.countryCode,
		email: values?.email || "",
		extraInfo: values?.extraInfo || [],
	};
};

const ContactSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Too Short!")
		.max(50, "Too Long!")
		.required("Required"),
	countryCode: Yup.string().required("Required"),
	number: Yup.string()
		.min(6, "Too Short!")
		.max(15, "Too Long!")
		.matches(/^\d+$/, "Only numbers are allowed.")
		.required("Required"),
	email: Yup.string().email("Invalid email"),
});

const ContactModal = ({ open, onClose, contactId, initialValues, onSave }) => {
	const classes = useStyles();
	const isMounted = useRef(true);

	const initialState = {
		name: "",
		number: "",
		countryCode: DEFAULT_COUNTRY_CODE,
		email: "",
		extraInfo: [],
	};

	const [contact, setContact] = useState(initialState);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		const fetchContact = async () => {
			if (initialValues) {
				setContact(prevState => ({
					...prevState,
					...buildContactState(initialValues),
				}));
			}

			if (!contactId) return;

			try {
				const { data } = await api.get(`/contacts/${contactId}`);
				if (isMounted.current) {
					setContact(buildContactState(data));
				}
			} catch (err) {
				toastError(err);
			}
		};

		fetchContact();
	}, [contactId, open, initialValues]);

	const handleClose = () => {
		onClose();
		setContact(initialState);
	};

	const handleSaveContact = async values => {
		const payload = {
			...values,
			number: sanitizeDigits(values.number),
			countryCode: sanitizeDigits(values.countryCode),
		};

		try {
			if (contactId) {
				await api.put(`/contacts/${contactId}`, payload);
				handleClose();
			} else {
				const { data } = await api.post("/contacts", payload);
				if (onSave) {
					onSave(data);
				}
				handleClose();
			}
			toast.success(i18n.t("contactModal.success"));
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<div className={classes.root}>
			<Dialog open={open} onClose={handleClose} maxWidth="lg" scroll="paper">
				<DialogTitle id="form-dialog-title">
					{contactId
						? `${i18n.t("contactModal.title.edit")}`
						: `${i18n.t("contactModal.title.add")}`}
				</DialogTitle>
				<Formik
					initialValues={contact}
					enableReinitialize={true}
					validationSchema={ContactSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveContact(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ values, errors, touched, isSubmitting }) => (
						<Form>
							<DialogContent dividers>
								<Typography variant="subtitle1" gutterBottom>
									{i18n.t("contactModal.form.mainInfo")}
								</Typography>
								<Field
									as={TextField}
									label={i18n.t("contactModal.form.name")}
									name="name"
									autoFocus
									error={touched.name && Boolean(errors.name)}
									helperText={touched.name && errors.name}
									variant="outlined"
									margin="dense"
									className={classes.textField}
								/>
								<div className={classes.phoneRow}>
									<Field
										as={TextField}
										select
										name="countryCode"
										label={i18n.t("contactModal.form.countryCode")}
										variant="outlined"
										margin="dense"
										className={classes.countryField}
										error={touched.countryCode && Boolean(errors.countryCode)}
										helperText={touched.countryCode && errors.countryCode}
										SelectProps={{ native: true }}
									>
										{COUNTRY_OPTIONS.map(option => (
											<option key={option.code} value={option.code}>
												{option.label}
											</option>
										))}
									</Field>
									<Field
										as={TextField}
										label={i18n.t("contactModal.form.number")}
										name="number"
										error={touched.number && Boolean(errors.number)}
										helperText={
											(touched.number && errors.number) ||
											i18n.t("contactModal.form.numberHint")
										}
										placeholder="3206792702"
										variant="outlined"
										margin="dense"
										className={classes.textField}
									/>
								</div>
								<div>
									<Field
										as={TextField}
										label={i18n.t("contactModal.form.email")}
										name="email"
										error={touched.email && Boolean(errors.email)}
										helperText={touched.email && errors.email}
										placeholder="Email address"
										fullWidth
										margin="dense"
										variant="outlined"
									/>
								</div>
								<Typography
									style={{ marginBottom: 8, marginTop: 12 }}
									variant="subtitle1"
								>
									{i18n.t("contactModal.form.extraInfo")}
								</Typography>

								<FieldArray name="extraInfo">
									{({ push, remove }) => (
										<>
											{values.extraInfo &&
												values.extraInfo.length > 0 &&
												values.extraInfo.map((info, index) => (
													<div
														className={classes.extraAttr}
														key={`${index}-info`}
													>
														<Field
															as={TextField}
															label={i18n.t("contactModal.form.extraName")}
															name={`extraInfo[${index}].name`}
															variant="outlined"
															margin="dense"
															className={classes.textField}
														/>
														<Field
															as={TextField}
															label={i18n.t("contactModal.form.extraValue")}
															name={`extraInfo[${index}].value`}
															variant="outlined"
															margin="dense"
															className={classes.textField}
														/>
														<IconButton
															size="small"
															onClick={() => remove(index)}
														>
															<DeleteOutlineIcon />
														</IconButton>
													</div>
												))}
											<div className={classes.extraAttr}>
												<Button
													style={{ flex: 1, marginTop: 8 }}
													variant="outlined"
													color="primary"
													onClick={() => push({ name: "", value: "" })}
												>
													{`+ ${i18n.t("contactModal.buttons.addExtraInfo")}`}
												</Button>
											</div>
										</>
									)}
								</FieldArray>
							</DialogContent>
							<DialogActions>
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
								>
									{i18n.t("contactModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={classes.btnWrapper}
								>
									{contactId
										? `${i18n.t("contactModal.buttons.okEdit")}`
										: `${i18n.t("contactModal.buttons.okAdd")}`}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default ContactModal;
